import {
   Table, Button, Modal, Form, Input, Select,
   message, Space, Popconfirm, Tag, Badge, Row, Col
} from "antd";
import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import {
   EditOutlined, DeleteOutlined, PlusOutlined,
   UserOutlined, TeamOutlined, CheckCircleOutlined,
   CloseCircleOutlined, LockOutlined, UnlockOutlined,
   SearchOutlined, FilterOutlined, ClearOutlined
} from "@ant-design/icons";
import {
   getAllUsers, registerUser, updateUser,
   deleteUser, approveUser, toggleUserStatus
} from "../../services/usersApi";

const { Search } = Input;
const { Option } = Select;

const Users = () => {
   const { user } = useContext(AuthContext);
   const [users, setUsers] = useState([]);
   const [filteredUsers, setFilteredUsers] = useState([]);
   const [open, setOpen] = useState(false);
   const [editingUser, setEditingUser] = useState(null);
   const [currentPage, setCurrentPage] = useState(1);
   const [loading, setLoading] = useState(false);
   const [form] = Form.useForm();

   // Filter States
   const [searchText, setSearchText] = useState("");
   const [roleFilter, setRoleFilter] = useState("all");
   const [statusFilter, setStatusFilter] = useState("all");
   const [approvalFilter, setApprovalFilter] = useState("all");

   const fetchUsers = async () => {
      setLoading(true);
      try {
         const { data } = await getAllUsers();
         setUsers(data);
         setFilteredUsers(data);
      } catch (error) {
         message.error("Failed to fetch users");
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchUsers();
   }, []);

   // Apply Filters
   useEffect(() => {
      let filtered = [...users];

      if (searchText) {
         filtered = filtered.filter(u =>
            u.name.toLowerCase().includes(searchText.toLowerCase()) ||
            u.email.toLowerCase().includes(searchText.toLowerCase())
         );
      }

      if (roleFilter !== "all") {
         filtered = filtered.filter(u => u.role_name === roleFilter);
      }

      if (statusFilter !== "all") {
         filtered = filtered.filter(u => u.status === statusFilter);
      }

      if (approvalFilter !== "all") {
         const isApproved = approvalFilter === "approved" ? 1 : 0;
         filtered = filtered.filter(u => u.is_approved === isApproved);
      }

      setFilteredUsers(filtered);
      setCurrentPage(1);
   }, [searchText, roleFilter, statusFilter, approvalFilter, users]);

   const handleClearFilters = () => {
      setSearchText("");
      setRoleFilter("all");
      setStatusFilter("all");
      setApprovalFilter("all");
   };

   const handleSubmit = async (values) => {
      try {
         if (editingUser) {
            await updateUser(editingUser.id, values);
            message.success("User updated successfully");
         } else {
            await registerUser(values);
            message.success("User added successfully");
         }
         setOpen(false);
         fetchUsers();
      } catch (err) {
         message.error(err.response?.data?.message || "Operation failed");
      }
   };

   const handleDelete = async (id) => {
      try {
         await deleteUser(id);
         message.success("User deleted successfully");
         fetchUsers();
      } catch (err) {
         message.error("Failed to delete user");
      }
   };

   const handleApprove = async (id) => {
      try {
         await approveUser(id);
         message.success("User approved successfully");
         fetchUsers();
      } catch (err) {
         message.error(err.response?.data?.message || "Approval failed");
      }
   };

   const handleToggleStatus = async (id, status) => {
      try {
         await toggleUserStatus(id, {
            status: status === "active" ? "disabled" : "active"
         });
         message.success("User status updated");
         fetchUsers();
      } catch (error) {
         message.error("Failed to update status");
      }
   };

   const columns = [
      {
         title: "#",
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">
               {(currentPage - 1) * 10 + index + 1}
            </span>
         )
      },
      {
         title: "Name",
         dataIndex: "name",
         render: (name) => (
            <div className="flex items-center gap-2">
               <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <UserOutlined className="text-blue-500 text-xs" />
               </div>
               <span className="font-medium text-gray-800">{name}</span>
            </div>
         )
      },
      {
         title: "Email",
         dataIndex: "email",
         render: (email) => (
            <span className="text-gray-600 text-sm">{email}</span>
         )
      },
      {
         title: "Role",
         dataIndex: "role_name",
         render: (role) => (
            <Tag
               color={role === "admin" ? "red" : "blue"}
               icon={role === "admin" ? <TeamOutlined /> : <UserOutlined />}
            >
               {role?.toUpperCase()}
            </Tag>
         )
      },
      {
         title: "Approval Status",
         dataIndex: "is_approved",
         align: "center",
         render: (value) => (
            <Tag
               color={value === 1 ? "green" : "orange"}
               icon={value === 1 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            >
               {value === 1 ? "Approved" : "Pending"}
            </Tag>
         )
      },
      {
         title: "Account Status",
         render: (_, record) => {
            if (record.is_approved === 0) {
               return <Tag color="orange">Awaiting Approval</Tag>;
            }
            return (
               <Badge
                  status={record.status === "active" ? "success" : "error"}
                  text={
                     <span className={record.status === "active" ? "text-green-600" : "text-red-600"}>
                        {record.status === "active" ? "Active" : "Disabled"}
                     </span>
                  }
               />
            );
         }
      },
      {
         title: "Action",
         width: 200,
         render: (_, record) => (
            <Space size="small">
               {record.is_approved === 0 && (
                  <Popconfirm
                     title="Approve User"
                     description="User will be able to login after approval."
                     onConfirm={() => handleApprove(record.id)}
                     okText="Approve"
                  >
                     <Button type="primary" size="small" icon={<CheckCircleOutlined />}>
                        Approve
                     </Button>
                  </Popconfirm>
               )}

               {record.is_approved === 1 && (
                  <Popconfirm
                     title={record.status === "active" ? "Disable this user?" : "Enable this user?"}
                     onConfirm={() => handleToggleStatus(record.id, record.status)}
                  >
                     <Button
                        danger={record.status === "active"}
                        type={record.status === "active" ? "default" : "primary"}
                        size="small"
                        icon={record.status === "active" ? <LockOutlined /> : <UnlockOutlined />}
                     >
                        {record.status === "active" ? "Disable" : "Enable"}
                     </Button>
                  </Popconfirm>
               )}

               <Button
                  type="text"
                  icon={<EditOutlined />}
                  className="text-blue-500 hover:text-blue-700"
                  onClick={() => {
                     setEditingUser(record);
                     form.setFieldsValue({
                        name: record.name,
                        email: record.email,
                        role_id: record.role_name === "admin" ? 1 : 2
                     });
                     setOpen(true);
                  }}
               />

               <Popconfirm
                  title="Delete User"
                  description="Are you sure you want to delete this user?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
               >
                  <Button danger type="text" icon={<DeleteOutlined />} />
               </Popconfirm>
            </Space>
         )
      }
   ];

   const stats = {
      total: users.length,
      approved: users.filter(u => u.is_approved === 1).length,
      pending: users.filter(u => u.is_approved === 0).length,
      active: users.filter(u => u.status === "active").length
   };

   const hasActiveFilters = searchText || roleFilter !== "all" || statusFilter !== "all" || approvalFilter !== "all";

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{`
            .users-card {
               background: #fff;
               border-radius: 10px;
               box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
               border: 1px solid #f3f4f6;
            }
            .users-table .ant-table-thead > tr > th {
               background: #f9fafb;
               font-size: 12px;
               font-weight: 600;
               padding: 12px 16px;
            }
            .users-table .ant-table-tbody > tr > td {
               padding: 12px 16px;
               font-size: 13px;
            }
            .users-table .ant-table-tbody > tr:hover > td {
               background: #f9fafb;
            }
            .stat-card {
               border-radius: 10px;
               transition: all 0.3s;
            }
            .stat-card:hover {
               transform: translateY(-2px);
               box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            }
         `}</style>

         {/* Header */}
         <div className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-xl font-semibold text-gray-800 m-0">User Management</h1>
                  <span className="text-gray-400 text-sm">Manage users and their permissions</span>
               </div>
               {user?.role === "admin" && (
                  <Button
                     type="primary"
                     icon={<PlusOutlined />}
                     onClick={() => {
                        setEditingUser(null);
                        form.resetFields();
                        setOpen(true);
                     }}
                  >
                     Add User
                  </Button>
               )}
            </div>
         </div>

         <div className="p-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
               <div className="users-card stat-card p-4">
                  <div className="flex items-center justify-between">
                     <div>
                        <div className="text-xs text-gray-500 mb-1">Total Users</div>
                        <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
                     </div>
                     <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <TeamOutlined className="text-blue-500 text-lg" />
                     </div>
                  </div>
               </div>

               <div className="users-card stat-card p-4">
                  <div className="flex items-center justify-between">
                     <div>
                        <div className="text-xs text-gray-500 mb-1">Approved</div>
                        <div className="text-2xl font-bold text-gray-800">{stats.approved}</div>
                     </div>
                     <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckCircleOutlined className="text-green-500 text-lg" />
                     </div>
                  </div>
               </div>

               <div className="users-card stat-card p-4">
                  <div className="flex items-center justify-between">
                     <div>
                        <div className="text-xs text-gray-500 mb-1">Pending Approval</div>
                        <div className="text-2xl font-bold text-gray-800">{stats.pending}</div>
                     </div>
                     <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <CloseCircleOutlined className="text-orange-500 text-lg" />
                     </div>
                  </div>
               </div>

               <div className="users-card stat-card p-4">
                  <div className="flex items-center justify-between">
                     <div>
                        <div className="text-xs text-gray-500 mb-1">Active Users</div>
                        <div className="text-2xl font-bold text-gray-800">{stats.active}</div>
                     </div>
                     <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <UnlockOutlined className="text-purple-500 text-lg" />
                     </div>
                  </div>
               </div>
            </div>

            {/* Filters Section */}
            <div className="users-card p-4 mb-4">
               <div className="flex items-center gap-2 mb-3">
                  <FilterOutlined className="text-gray-600" />
                  <span className="font-medium text-gray-700">Filters</span>
                  {hasActiveFilters && (
                     <Tag color="blue">{filteredUsers.length} results</Tag>
                  )}
               </div>

               <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} lg={6}>
                     <Search
                        placeholder="Search by name or email"
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        prefix={<SearchOutlined className="text-gray-400" />}
                     />
                  </Col>

                  <Col xs={24} sm={12} lg={4}>
                     <Select
                        value={roleFilter}
                        onChange={setRoleFilter}
                        style={{ width: "100%" }}
                        placeholder="Filter by role"
                     >
                        <Option value="all">All Roles</Option>
                        <Option value="admin">Admin</Option>
                        <Option value="staff">Staff</Option>
                     </Select>
                  </Col>

                  <Col xs={24} sm={12} lg={5}>
                     <Select
                        value={approvalFilter}
                        onChange={setApprovalFilter}
                        style={{ width: "100%" }}
                        placeholder="Filter by approval"
                     >
                        <Option value="all">All Status</Option>
                        <Option value="approved">Approved</Option>
                        <Option value="pending">Pending</Option>
                     </Select>
                  </Col>

                  <Col xs={24} sm={12} lg={5}>
                     <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        style={{ width: "100%" }}
                        placeholder="Filter by status"
                     >
                        <Option value="all">All Accounts</Option>
                        <Option value="active">Active</Option>
                        <Option value="disabled">Disabled</Option>
                     </Select>
                  </Col>

                  <Col xs={24} sm={12} lg={4}>
                     <Button
                        icon={<ClearOutlined />}
                        onClick={handleClearFilters}
                        disabled={!hasActiveFilters}
                        block
                     >
                        Clear Filters
                     </Button>
                  </Col>
               </Row>
            </div>

            {/* Table Card */}
            <div className="users-card users-table">
               <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                     <div className="flex items-center gap-2">
                        <TeamOutlined className="text-gray-600" />
                        <span className="font-medium text-gray-700">
                           {hasActiveFilters ? "Filtered Users" : "All Users"}
                        </span>
                        <Tag>{filteredUsers.length}</Tag>
                     </div>
                     {hasActiveFilters && (
                        <span className="text-xs text-gray-500">
                           Showing {filteredUsers.length} of {users.length} users
                        </span>
                     )}
                  </div>
               </div>

               <Table
                  dataSource={filteredUsers}
                  columns={columns}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                     current: currentPage,
                     onChange: (page) => setCurrentPage(page),
                     pageSize: 10,
                     showTotal: (total, range) => (
                        <span className="text-sm text-gray-500">
                           Showing <b>{range[0]}-{range[1]}</b> of <b>{total}</b> users
                        </span>
                     ),
                     size: "small"
                  }}
               />
            </div>
         </div>

         {/* Modal */}
         <Modal
            title={
               <div className="flex items-center gap-2">
                  <UserOutlined />
                  <span>{editingUser ? "Edit User" : "Add New User"}</span>
               </div>
            }
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            width={500}
         >
            <Form
               layout="vertical"
               form={form}
               onFinish={handleSubmit}
               className="mt-4"
            >
               <Form.Item
                  name="name"
                  label="Full Name"
                  rules={[{ required: true, message: "Please enter name" }]}
               >
                  <Input
                     prefix={<UserOutlined className="text-gray-400" />}
                     placeholder="Enter full name"
                  />
               </Form.Item>

               <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                     { required: true, message: "Please enter email" },
                     { type: "email", message: "Please enter valid email" }
                  ]}
               >
                  <Input
                     prefix={<span className="text-gray-400">@</span>}
                     placeholder="Enter email address"
                  />
               </Form.Item>

               {!editingUser && (
                  <Form.Item
                     name="password"
                     label="Password"
                     rules={[
                        { required: true, message: "Please enter password" },
                        { min: 6, message: "Password must be at least 6 characters" }
                     ]}
                  >
                     <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder="Enter password"
                     />
                  </Form.Item>
               )}

               <Form.Item
                  name="role_id"
                  label="User Role"
                  rules={[{ required: true, message: "Please select role" }]}
               >
                  <Select placeholder="Select role">
                     <Select.Option value={1}>Admin</Select.Option>
                     <Select.Option value={2}>Staff</Select.Option>
                  </Select>
               </Form.Item>

               <Form.Item className="mb-0">
                  <Space className="w-full justify-end">
                     <Button onClick={() => setOpen(false)}>Cancel</Button>
                     <Button type="primary" htmlType="submit">
                        {editingUser ? "Update User" : "Create User"}
                     </Button>
                  </Space>
               </Form.Item>
            </Form>
         </Modal>
      </div>
   );
};

export default Users;
